import React, { useState, useEffect, useMemo } from 'react';
import { getProgramById } from '../data/programs';
import { set1RM, saveSettings } from '../utils/settings';
import {
  calcWeight, generateWarmup, epley1RM, formatWeight, getFirstWorkingWeight,
  getMaxAdjustment, rmToMax, parseRMType, roundWeight,
  MAIN_LIFT_NAMES, THREE_TIER_PROGRAMS, PER_DAY_WEIGHT_PROGRAMS, PER_DAY_PERCENTAGES
} from '../utils/calculations';
import { addLogEntry, setProgramProgress, addPR, getPRsByLift } from '../db';
import { getCuesForExercise } from '../data/cues';

const RM_ESTIMATES = {
  '15RM': 0.60, '12RM': 0.65, '10RM': 0.70, '8RM': 0.75,
  '6RM': 0.80, '5RM': 0.85, '3RM': 0.90, '1RM': 1.0,
  '30RM': 0.45, '20RM': 0.55,
};

export default function HomeTab({ settings, logEntries, bodyweights, prs, progress, refresh, setTab }) {
  const [logging, setLogging] = useState(false);
  const [logData, setLogData] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [cycleComplete, setCycleComplete] = useState({});
  const [cycleTestedMax, setCycleTestedMax] = useState({}); // { bench: 145 } — filled when 1RM test logged
  const [newMaxes, setNewMaxes] = useState({ bench: '', squat: '', deadlift: '' });
  const [maxAdjustmentMsg, setMaxAdjustmentMsg] = useState(null);
  const [editingLiftNote, setEditingLiftNote] = useState(false);
  const [liftNoteDraft, setLiftNoteDraft] = useState('');
  const [backupDismissed, setBackupDismissed] = useState(false);
  const [cuesOpen, setCuesOpen] = useState({}); // { exerciseIndex: true }

  const [activeLift, setActiveLift] = useState('bench');
  const lifts = ['bench', 'squat', 'deadlift'];
  const liftColors = { bench: 'var(--bench-color)', squat: 'var(--squat-color)', deadlift: 'var(--deadlift-color)' };

  const shouldShowDeload = useMemo(() => {
    if (logEntries.length === 0) return false;
    const dates = [...new Set(logEntries.map(e => e.date))].sort();
    if (dates.length < 12) return false;
    const lastDate = new Date(dates[dates.length - 1]);
    const fourWeeksAgo = new Date(lastDate);
    fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
    const recentSessions = dates.filter(d => new Date(d) >= fourWeeksAgo).length;
    return recentSessions >= 10;
  }, [logEntries]);

  const shouldShowBackupReminder = useMemo(() => {
    if (backupDismissed) return false;
    const lastBackupCount = settings.lastBackupSessionCount;
    if (lastBackupCount == null && logEntries.length >= 10) return true;
    if (lastBackupCount != null && (logEntries.length - lastBackupCount) >= 10) return true;
    return false;
  }, [logEntries.length, settings.lastBackupSessionCount, backupDismissed]);

  const programId = settings[`${activeLift}Program`];
  const program = getProgramById(programId);
  const liftProgress = progress[activeLift] || { weekIndex: 0, dayIndex: 0, cycle: 1 };
  const oneRM = settings[`${activeLift}1RM`] || 0;
  const rounding = settings.rounding || 2.5;

  const currentWeek = program?.weeks?.[liftProgress.weekIndex];
  const currentDay = currentWeek?.days?.[liftProgress.dayIndex];
  const isAtEndOfProgram = !currentWeek || !currentDay;
  const isMaxTestWeek = currentWeek?.isMaxTest;

  // Per-day weight program detection
  const isPerDayProgram = PER_DAY_WEIGHT_PROGRAMS.includes(programId);

  // Get or initialize per-day weights
  const dayWeights = useMemo(() => {
    if (!isPerDayProgram) return null;
    if (liftProgress.dayWeights) return liftProgress.dayWeights;
    // Initialize from training max
    const weights = {};
    for (const [dayIdx, pct] of Object.entries(PER_DAY_PERCENTAGES)) {
      weights[dayIdx] = calcWeight(pct, oneRM, rounding);
    }
    return weights;
  }, [isPerDayProgram, liftProgress.dayWeights, oneRM, rounding]);

  // Initialize dayWeights in DB if missing (migration for older data)
  // Guard: only run when progress has actually loaded from IndexedDB
  useEffect(() => {
    if (isPerDayProgram && progress[activeLift] && !progress[activeLift].dayWeights && dayWeights) {
      setProgramProgress(activeLift, { ...progress[activeLift], dayWeights });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPerDayProgram, activeLift, progress[activeLift]]);

  // Current day's working weight for per-day programs
  const perDayWeight = isPerDayProgram && dayWeights ? dayWeights[liftProgress.dayIndex] || null : null;

  // Last session references
  const lastSessionByExercise = useMemo(() => {
    const map = {};
    const relevant = logEntries
      .filter(e => e.lift === activeLift && e.programId === programId)
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    const sameDayEntries = relevant.filter(
      e => e.weekNum === currentWeek?.weekNum && e.dayNum === currentDay?.dayNum
    );
    const prevWeekNum = currentWeek?.weekNum ? currentWeek.weekNum - 1 : 0;
    const prevWeekEntries = relevant.filter(e => e.weekNum === prevWeekNum);
    const w1Entries = relevant.filter(e => e.weekNum === 1);

    const processEntries = (entries, prefix) => {
      for (const entry of entries) {
        if (!entry.exercises) continue;
        for (const ex of entry.exercises) {
          const key = `${prefix}_${ex.name}`;
          if (!map[key]) {
            map[key] = { weight: ex.loggedWeight, reps: ex.loggedReps, date: entry.date };
          }
        }
      }
    };

    processEntries(sameDayEntries, 'same');
    processEntries(prevWeekEntries, 'prev');
    processEntries(w1Entries, 'w1');
    processEntries(relevant, 'any');
    return map;
  }, [logEntries, activeLift, programId, currentWeek, currentDay]);

  const getLastRef = (exName) => {
    const same = lastSessionByExercise[`same_${exName}`];
    if (same && same.weight > 0) return same;
    const any = lastSessionByExercise[`any_${exName}`];
    if (any && any.weight > 0) return any;
    return null;
  };

  const getPrevWeekRef = (exName) => lastSessionByExercise[`prev_${exName}`] || null;
  const getW1Ref = (exName) => lastSessionByExercise[`w1_${exName}`] || null;

  // Build exercise list
  const sessionExercises = useMemo(() => {
    if (!currentDay) return [];
    return currentDay.exercises.map((ex, i) => {
      let weight = null;
      let displayWeight = '';
      let rmSuggestion = null;
      let weightHint = null;

      if (ex.percentage) {
        if (isPerDayProgram && perDayWeight) {
          // Use per-day tracked weight instead of percentage * 1RM
          weight = perDayWeight;
          displayWeight = `${formatWeight(weight)}kg`;
        } else {
          weight = calcWeight(ex.percentage, oneRM, rounding);
          displayWeight = `${formatWeight(weight)}kg`;
        }
      } else if (typeof ex.weight === 'number') {
        // Imported linear-progression programs prescribe an absolute weight
        weight = ex.weight;
        displayWeight = `${formatWeight(weight)}kg`;
      } else if (ex.weight) {
        displayWeight = ex.weight;
        // Only suggest RM weights for main lifts, not variants (front squat, close grip bench, etc.)
        const mainLiftNames = MAIN_LIFT_NAMES[activeLift] || [];
        const isMainLift = mainLiftNames.some(name => ex.name === name || ex.name.startsWith(name));
        const rmPct = RM_ESTIMATES[ex.weight];
        if (rmPct && isMainLift) rmSuggestion = roundWeight(oneRM * rmPct, rounding);

        if (typeof ex.weight === 'string') {
          if (ex.weight.toLowerCase() === 'heavier') {
            const prev = getPrevWeekRef(ex.name);
            if (prev && prev.weight > 0) weightHint = { type: 'heavier', lastWeight: prev.weight };
          }
          if (ex.weight.toLowerCase().includes('w1 weight')) {
            const w1 = getW1Ref(ex.name);
            if (w1 && w1.weight > 0) weightHint = { type: 'w1', weight: w1.weight };
          }
          if (ex.weight.toLowerCase() === 'same') weightHint = { type: 'same' };
        }
      }

      const lastRef = getLastRef(ex.name);
      return { ...ex, calculatedWeight: weight, displayWeight, rmSuggestion, weightHint, lastRef, index: i };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDay, oneRM, rounding, lastSessionByExercise, isPerDayProgram, perDayWeight]);

  const firstWorkingWeight = useMemo(() => {
    if (isPerDayProgram && perDayWeight) return perDayWeight;
    return getFirstWorkingWeight(currentDay?.exercises || [], oneRM, rounding);
  }, [currentDay, oneRM, rounding, isPerDayProgram, perDayWeight]);

  const warmupSets = useMemo(() => generateWarmup(firstWorkingWeight, rounding), [firstWorkingWeight, rounding]);

  const amapExIndex = useMemo(() => {
    if (!currentDay) return -1;
    return currentDay.exercises.findIndex(ex => ex.isAMAP && !ex.isAccessory);
  }, [currentDay]);

  // Initialize log data
  useEffect(() => {
    const data = {};
    sessionExercises.forEach((ex, i) => {
      let prefill = ex.calculatedWeight || '';
      if (!prefill && ex.rmSuggestion) prefill = ex.rmSuggestion;
      if (!prefill && ex.weightHint?.type === 'w1') prefill = ex.weightHint.weight;

      // Pre-fill reps for fixed-rep exercises (not AMAP, not ranges like "8-10")
      let repsPrefill = '';
      const repsStr = String(ex.reps || '');
      if (!ex.isAMAP && repsStr !== 'AMAP' && repsStr !== 'AMAP-2' &&
          !repsStr.includes('-') && !repsStr.includes('AMAP') &&
          /^\d+$/.test(repsStr)) {
        repsPrefill = repsStr;
      }

      data[i] = { weight: prefill, reps: repsPrefill, completed: false, rpe: '' };
    });
    setLogData(data);
    setNotes('');
    setLogging(false);
    setMaxAdjustmentMsg(null);
    setEditingLiftNote(false);
    setCuesOpen({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLift, liftProgress.weekIndex, liftProgress.dayIndex]);

  const handleLogChange = (idx, field, value) => {
    setLogData(prev => {
      const updated = { ...prev, [idx]: { ...prev[idx], [field]: value } };
      if (field === 'weight') {
        const sourceEx = sessionExercises[idx];
        if (sourceEx && (sourceEx.weight?.toString().match(/\dRM$/i))) {
          sessionExercises.forEach((ex, i) => {
            if (i > idx && ex.weightHint?.type === 'same' && ex.name === sourceEx.name) {
              updated[i] = { ...updated[i], weight: value };
            }
          });
        }
      }
      return updated;
    });
  };

  const getAmapMinusTwoTarget = () => {
    if (amapExIndex < 0) return null;
    const amapReps = parseInt(logData[amapExIndex]?.reps);
    if (!amapReps || amapReps < 3) return null;
    return amapReps - 2;
  };

  const handleSaveSession = async () => {
    if (saving) return;
    setSaving(true);

    try {
      const date = new Date().toISOString().split('T')[0];
      const exercises = sessionExercises.map((ex, i) => ({
        name: ex.name,
        prescribedWeight: ex.calculatedWeight,
        prescribedSets: ex.sets,
        prescribedReps: ex.reps,
        loggedWeight: parseFloat(logData[i]?.weight) || 0,
        loggedReps: parseInt(logData[i]?.reps) || 0,
        loggedSets: ex.sets,
        rpe: logData[i]?.rpe || null,
        isAMAP: ex.isAMAP || false,
        isAccessory: ex.isAccessory || false,
        isMaxTest: ex.isMaxTest || false,
        percentage: ex.percentage || null,
      }));

      let estimatedMax = null;
      for (const ex of exercises) {
        if ((ex.isAMAP || ex.isMaxTest) && ex.loggedWeight > 0 && ex.loggedReps > 0) {
          const est = epley1RM(ex.loggedWeight, ex.loggedReps);
          if (!estimatedMax || est > estimatedMax) estimatedMax = est;
        }
      }

      let tonnage = 0;
      for (const ex of exercises) {
        if (ex.loggedWeight > 0 && ex.loggedReps > 0) {
          tonnage += ex.loggedWeight * ex.loggedReps * ex.loggedSets;
        }
      }

      let isPR = false;
      if (estimatedMax) {
        const liftPRs = await getPRsByLift(activeLift);
        const bestPR = liftPRs.reduce((max, pr) => Math.max(max, pr.estimatedMax), 0);
        if (estimatedMax > bestPR && estimatedMax > oneRM) {
          isPR = true;
          await addPR({
            lift: activeLift, date, estimatedMax,
            achievedWith: exercises.find(e => e.isAMAP || e.isMaxTest),
          });
        }
      }

      await addLogEntry({
        date, lift: activeLift, programId,
        programName: program?.displayName,
        weekNum: currentWeek?.weekNum, dayNum: currentDay?.dayNum,
        cycle: liftProgress.cycle, exercises, estimatedMax, tonnage, isPR, notes,
      });

      // === AUTO-REGULATION ===
      let adjustmentApplied = 0;
      const mainLiftNames = MAIN_LIFT_NAMES[activeLift] || [];
      const isThreeTier = THREE_TIER_PROGRAMS.includes(programId);

      if (isPerDayProgram) {
        // Per-day weight programs: adjust only this day's weight
        for (const ex of exercises) {
          if (!ex.isAMAP || ex.isAccessory) continue;
          if (!mainLiftNames.some(name => ex.name === name || ex.name.startsWith(name))) continue;
          if (!ex.percentage || ex.loggedReps <= 0) continue;

          const adjustment = getMaxAdjustment(ex.percentage, ex.loggedReps, isThreeTier);
          const currentDayIdx = liftProgress.dayIndex;
          const currentWeight = dayWeights[currentDayIdx] || perDayWeight || 0;
          const newWeight = roundWeight(currentWeight + adjustment, rounding);

          const updatedDayWeights = { ...dayWeights, [currentDayIdx]: newWeight };

          // Advance progress with updated dayWeights
          const totalDays = currentWeek.days.length;
          const totalWeeks = program.weeks.length;
          let nextWeekIndex = liftProgress.weekIndex;
          let nextDayIndex = liftProgress.dayIndex + 1;
          if (nextDayIndex >= totalDays) { nextDayIndex = 0; nextWeekIndex++; }

          if (nextWeekIndex >= totalWeeks) {
            // Cycle complete - reset to week 0 but keep dayWeights
            setCycleComplete(prev => ({ ...prev, [activeLift]: true }));
            // Store updated dayWeights so they persist into the next cycle
            await setProgramProgress(activeLift, {
              ...liftProgress, dayWeights: updatedDayWeights,
            });
          } else {
            await setProgramProgress(activeLift, {
              weekIndex: nextWeekIndex, dayIndex: nextDayIndex,
              cycle: liftProgress.cycle, dayWeights: updatedDayWeights,
            });
          }

          if (adjustment > 0) {
            adjustmentApplied = adjustment;
            setMaxAdjustmentMsg(
              `Day ${currentDayIdx + 1} weight updated: ${formatWeight(currentWeight)}kg → ${formatWeight(newWeight)}kg (+${formatWeight(adjustment)}kg)`
            );
          } else {
            setMaxAdjustmentMsg(
              `Day ${currentDayIdx + 1} weight stays at ${formatWeight(currentWeight)}kg`
            );
          }

          refresh();
          return; // Exit early - progress already advanced
        }
      } else {
        // Standard programs: adjust single training max
        for (const ex of exercises) {
          if (!ex.isAMAP || ex.isAccessory) continue;
          if (!mainLiftNames.some(name => ex.name === name || ex.name.startsWith(name))) continue;
          if (!ex.percentage || ex.loggedReps <= 0) continue;
          const adjustment = getMaxAdjustment(ex.percentage, ex.loggedReps, isThreeTier);
          if (adjustment > 0) {
            set1RM(activeLift, oneRM + adjustment);
            adjustmentApplied = adjustment;
          }
        }

        // RM-based max recalculation
        if (!adjustmentApplied) {
          for (const ex of exercises) {
            if (ex.isAccessory) continue;
            if (!mainLiftNames.some(name => ex.name === name || ex.name.startsWith(name))) continue;
            const rmType = parseRMType(sessionExercises.find(se => se.name === ex.name)?.weight);
            if (rmType && ex.loggedWeight > 0) {
              const suggestedMax = rmToMax(ex.loggedWeight, rmType, rounding);
              if (suggestedMax && suggestedMax !== oneRM) {
                set1RM(activeLift, suggestedMax);
                adjustmentApplied = suggestedMax - oneRM;
              }
            }
          }
        }

        if (adjustmentApplied) {
          const newMax = oneRM + adjustmentApplied;
          setMaxAdjustmentMsg(
            `Training max updated: ${formatWeight(oneRM)}kg → ${formatWeight(newMax)}kg (${adjustmentApplied > 0 ? '+' : ''}${formatWeight(adjustmentApplied)}kg)`
          );
        }
      }

      // Advance program progress (for non-per-day programs, or if no AMAP was found)
      const totalDays = currentWeek.days.length;
      const totalWeeks = program.weeks.length;
      let nextWeekIndex = liftProgress.weekIndex;
      let nextDayIndex = liftProgress.dayIndex + 1;
      if (nextDayIndex >= totalDays) { nextDayIndex = 0; nextWeekIndex++; }

      // Detect 1RM test result
      let testedMax = null;
      for (const ex of exercises) {
        if (ex.isMaxTest && ex.loggedWeight > 0 && ex.loggedReps >= 1) {
          testedMax = ex.loggedWeight;
        }
      }

      if (nextWeekIndex >= totalWeeks) {
        // Store tested max for cycle completion screen
        if (testedMax) {
          setCycleTestedMax(prev => ({ ...prev, [activeLift]: testedMax }));
        }
        setCycleComplete(prev => ({ ...prev, [activeLift]: true }));
      } else {
        await setProgramProgress(activeLift, {
          weekIndex: nextWeekIndex, dayIndex: nextDayIndex,
          cycle: liftProgress.cycle,
          ...(isPerDayProgram && dayWeights ? { dayWeights } : {}),
        });
      }
      refresh();
    } finally { setSaving(false); }
  };

  const handleCycleReset = async (lift) => {
    const userInput = parseFloat(newMaxes[lift]);
    const tested = cycleTestedMax[lift];
    // Priority: user override > tested max > keep current
    const newMax = (userInput && userInput >= 20) ? userInput
      : (tested && tested >= 20) ? tested
      : null;

    if (newMax) set1RM(lift, newMax);

    // For per-day programs, recalculate dayWeights from the new max
    const effectiveMax = newMax || oneRM;
    let resetDayWeights = undefined;
    if (isPerDayProgram) {
      if (newMax) {
        resetDayWeights = {};
        for (const [dayIdx, pct] of Object.entries(PER_DAY_PERCENTAGES)) {
          resetDayWeights[dayIdx] = calcWeight(pct, effectiveMax, rounding);
        }
      } else {
        resetDayWeights = dayWeights;
      }
    }

    await setProgramProgress(lift, {
      weekIndex: 0, dayIndex: 0, cycle: (liftProgress.cycle || 1) + 1,
      ...(resetDayWeights ? { dayWeights: resetDayWeights } : {}),
    });
    setCycleComplete(prev => ({ ...prev, [lift]: false }));
    setCycleTestedMax(prev => ({ ...prev, [lift]: undefined }));
    setNewMaxes(prev => ({ ...prev, [lift]: '' }));
    setMaxAdjustmentMsg(null);
    refresh();
  };

  if (cycleComplete[activeLift] || isAtEndOfProgram) {
    const testedMax = cycleTestedMax[activeLift];
    const liftName = activeLift.charAt(0).toUpperCase() + activeLift.slice(1);
    const diff = testedMax ? testedMax - oneRM : 0;

    return (
      <div>
        <div className="session-header">
          <h1>Cycle Complete!</h1>
          <p className="sub">{program?.displayName}</p>
        </div>

        {testedMax ? (
          /* === Prescribed 1RM test result === */
          <div style={{
            background: 'var(--bg-elevated)', border: '1px solid var(--amap-border)',
            borderRadius: 10, padding: 20, textAlign: 'center', marginBottom: 16,
          }}>
            <h3 style={{
              fontFamily: 'Barlow Condensed, sans-serif', fontSize: 20,
              fontWeight: 600, margin: '0 0 12px',
            }}>{liftName} 1RM Test Result</h3>

            <div style={{
              fontFamily: 'Barlow Condensed, sans-serif', fontSize: 48,
              fontWeight: 700, color: liftColors[activeLift], lineHeight: 1, marginBottom: 4,
            }}>{formatWeight(testedMax)}kg</div>

            <div style={{ fontSize: 14, color: 'var(--text-dim)', marginBottom: 16 }}>
              Previous 1RM: {formatWeight(oneRM)}kg
              <span style={{
                marginLeft: 8, fontWeight: 700,
                color: diff > 0 ? 'var(--success)' : diff < 0 ? 'var(--danger)' : 'var(--text-dim)',
              }}>
                {diff > 0 ? '+' : ''}{formatWeight(diff)}kg
              </span>
            </div>

            {diff < 0 && (
              <div style={{
                background: 'rgba(255,74,74,0.1)', border: '1px solid rgba(255,74,74,0.2)',
                borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                fontSize: 13, color: '#ff8888', lineHeight: 1.5, textAlign: 'left',
              }}>
                Your tested 1RM was lower than your previous training max. This can happen — bad day, fatigue, or the training max was running slightly high. Your weights will update so next cycle is accurate.
              </div>
            )}

            {diff > 0 && (
              <div style={{
                background: 'rgba(74,255,127,0.1)', border: '1px solid rgba(74,255,127,0.2)',
                borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                fontSize: 13, color: 'var(--success)', lineHeight: 1.5, textAlign: 'left',
              }}>
                {diff >= 10 ? 'Big jump! ' : ''}Your 1RM and program weights will update automatically.
              </div>
            )}

            <div style={{
              fontSize: 12, color: 'var(--text-muted)', marginBottom: 12,
            }}>
              Auto-filled from your test. Override below if needed:
            </div>
            <div className="rm-input-row" style={{justifyContent:'center', marginBottom: 16}}>
              <input type="number" style={{width:100, textAlign:'center'}}
                value={newMaxes[activeLift] || testedMax}
                onChange={e => setNewMaxes(prev => ({ ...prev, [activeLift]: e.target.value }))}
                onFocus={e => e.target.select()} />
              <span style={{color:'var(--text-dim)'}}>kg</span>
            </div>

            <button className="btn btn-primary" onClick={() => handleCycleReset(activeLift)}>Start Next Cycle</button>
          </div>
        ) : (
          /* === No prescribed test === */
          <div style={{
            background: 'var(--bg-elevated)', border: '1px solid var(--amap-border)',
            borderRadius: 10, padding: 20, textAlign: 'center', marginBottom: 16,
          }}>
            <div style={{
              fontFamily: 'Barlow Condensed, sans-serif', fontSize: 14,
              fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em',
              color: 'var(--text-dim)', marginBottom: 12,
            }}>Cycle Summary</div>

            <div style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.5, marginBottom: 16 }}>
              Your program weights have been auto-adjusting throughout the cycle. Ready to continue.
            </div>

            {isPerDayProgram && dayWeights && (
              <div style={{fontSize:12, color:'var(--text-dim)', marginBottom:12}}>
                Current day weights: {Object.entries(dayWeights).map(([d, w]) =>
                  `D${parseInt(d)+1}: ${formatWeight(w)}kg`
                ).join(' · ')}
              </div>
            )}

            <div style={{
              background: 'var(--bg)', borderRadius: 8, padding: '10px 14px',
              marginBottom: 12, textAlign: 'left',
            }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                If you've tested your 1RM outside the program, enter it below:
              </div>
              <div className="rm-input-row" style={{justifyContent:'center'}}>
                <input type="number" style={{width:100, textAlign:'center'}}
                  value={newMaxes[activeLift]}
                  onChange={e => setNewMaxes(prev => ({ ...prev, [activeLift]: e.target.value }))}
                  onFocus={e => e.target.select()} placeholder="optional" />
                <span style={{color:'var(--text-dim)'}}>kg</span>
              </div>
            </div>

            <button className="btn btn-primary" onClick={() => handleCycleReset(activeLift)}>Start Next Cycle</button>
          </div>
        )}
        <LiftSelector lifts={lifts} active={activeLift} onSelect={setActiveLift} settings={settings} />
      </div>
    );
  }

  const amapMinusTwoTarget = getAmapMinusTwoTarget();

  return (
    <div>
      {shouldShowDeload && (
        <div className="deload-banner">
          <span className="deload-banner-icon">⚡</span>
          <span className="deload-banner-text">You've been training hard — consider a lighter week before your next cycle.</span>
        </div>
      )}

      {shouldShowBackupReminder && (
        <div style={{
          background: 'rgba(74,158,255,0.1)', border: '1px solid rgba(74,158,255,0.25)',
          borderRadius: 10, padding: '10px 14px', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ flex: 1, fontSize: 13, color: '#5aadff', lineHeight: 1.4 }}>
            You've logged {logEntries.length - (settings.lastBackupSessionCount || 0)} sessions since your last backup.
          </div>
          <button style={{
            background: 'rgba(74,158,255,0.2)', border: '1px solid rgba(74,158,255,0.4)',
            borderRadius: 6, padding: '6px 12px', color: '#5aadff',
            fontFamily: 'Barlow Condensed, sans-serif', fontSize: 12, fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer', whiteSpace: 'nowrap',
          }} onClick={() => setTab('profile')}>Export</button>
          <button style={{
            background: 'none', border: 'none', color: '#5aadff55',
            fontSize: 16, cursor: 'pointer', padding: '2px 4px',
          }} onClick={() => setBackupDismissed(true)}>✕</button>
        </div>
      )}

      {maxAdjustmentMsg && (
        <div style={{
          background: 'rgba(74,255,127,0.1)', border: '1px solid rgba(74,255,127,0.3)',
          borderRadius: 10, padding: '10px 14px', marginBottom: 12,
          fontSize: 13, color: 'var(--success)', textAlign: 'center',
        }}>{maxAdjustmentMsg}</div>
      )}

      <div className="session-header">
        <h1>{activeLift.charAt(0).toUpperCase() + activeLift.slice(1)}</h1>
        <p className="sub">
          Week {currentWeek?.weekNum} · Day {currentDay?.dayNum}
          {isMaxTestWeek && ' · Max Test'} · {program?.displayName}
        </p>
        <p className="sub" style={{marginTop:2}}>
          Training max: {formatWeight(oneRM)}kg
          {isPerDayProgram && perDayWeight && ` · Today: ${formatWeight(perDayWeight)}kg`}
        </p>
      </div>

      <LiftSelector lifts={lifts} active={activeLift} onSelect={setActiveLift} settings={settings} />

      {warmupSets.length > 0 && (
        <div style={{marginBottom: 8}}>
          <div className="card-header">Warm-up</div>
          {warmupSets.map((s, i) => (
            <div key={i} className="exercise-row warmup">
              <span className="warmup-label">W/U</span>
              <span className="exercise-name">Bar</span>
              <span className="exercise-detail exercise-weight">{formatWeight(s.weight)}kg</span>
              <span className="exercise-detail exercise-prescription">×{s.reps}</span>
            </div>
          ))}
        </div>
      )}

      <div className="card-header">Working Sets</div>
      {sessionExercises.map((ex, i) => {
        let displayReps = ex.reps;
        if (ex.reps === 'AMAP-2' && logging && amapMinusTwoTarget) {
          displayReps = `${amapMinusTwoTarget}`;
        }
        const cues = getCuesForExercise(ex.name);

        return (
          <div key={i} className={`exercise-row ${ex.isAMAP ? 'amap' : ex.isMaxTest ? 'max-test' : ex.isAccessory ? 'accessory' : 'working'}`}>
            <div style={{flex:1}}>
              <div className="exercise-name">
                {ex.name}
                {cues && (
                  <button onClick={() => setCuesOpen(p => ({ ...p, [i]: !p[i] }))} style={{
                    background: 'none', border: '1px solid var(--border)', borderRadius: '50%',
                    width: 16, height: 16, marginLeft: 6, padding: 0, cursor: 'pointer',
                    color: cuesOpen[i] ? 'var(--accent)' : 'var(--text-muted)', fontSize: 10,
                    lineHeight: '14px', verticalAlign: 'middle',
                  }} aria-label="Form cues">i</button>
                )}
                {ex.isAMAP && <span className="amap-badge" style={{marginLeft:6}}>AMAP</span>}
                {ex.reps === 'AMAP-2' && <span style={{
                  fontSize:10, color:'var(--amap-border)', marginLeft:6,
                  background:'rgba(204,136,0,0.15)', padding:'2px 5px', borderRadius:3,
                }}>AMAP-2</span>}
              </div>
              {ex.notes && <div className="notes-text">{ex.notes}</div>}
              {ex.weightHint?.type === 'heavier' && (
                <div style={{fontSize:11, color:'var(--amap-border)', marginTop:3}}>
                  Aim heavier than last time ({formatWeight(ex.weightHint.lastWeight)}kg)
                </div>
              )}
              {ex.weightHint?.type === 'w1' && (
                <div style={{fontSize:11, color:'var(--text-dim)', marginTop:3}}>
                  Week 1 weight: {formatWeight(ex.weightHint.weight)}kg
                </div>
              )}
              {ex.weightHint?.type === 'same' && logging && logData[i]?.weight && (
                <div style={{fontSize:11, color:'var(--text-dim)', marginTop:3}}>Same weight as RM set above</div>
              )}
              {ex.reps === 'AMAP-2' && !logging && (
                <div className="notes-text">Reps = your AMAP result minus 2</div>
              )}
              {ex.reps === 'AMAP-2' && logging && amapMinusTwoTarget && (
                <div style={{fontSize:12, color:'var(--success)', marginTop:2}}>Target: {amapMinusTwoTarget} reps (AMAP - 2)</div>
              )}
              {ex.reps === 'AMAP-2' && logging && !amapMinusTwoTarget && (
                <div style={{fontSize:12, color:'var(--text-muted)', marginTop:2}}>Log your AMAP set first</div>
              )}
              {ex.lastRef && ex.lastRef.weight > 0 && !ex.weightHint && (
                <div style={{fontSize:11, color:'var(--text-muted)', marginTop:3}}>
                  Last: {formatWeight(ex.lastRef.weight)}kg × {ex.lastRef.reps}
                </div>
              )}
              {cues && cuesOpen[i] && (
                <div style={{
                  marginTop: 6, padding: '8px 10px', background: 'var(--bg)',
                  borderRadius: 6, border: '1px solid var(--border)',
                }}>
                  {cues.map((c, ci) => (
                    <div key={ci} style={{ fontSize: 11, color: 'var(--text-dim)', padding: '2px 0', lineHeight: 1.4 }}>
                      · {c}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <span className="exercise-detail exercise-weight">
              {ex.displayWeight || '—'}
              {ex.rmSuggestion && !ex.calculatedWeight && (
                <span style={{color:'var(--text-dim)', fontSize:12, marginLeft:2}}>
                  ({formatWeight(ex.rmSuggestion)}kg)
                </span>
              )}
            </span>
            <span className="exercise-detail exercise-prescription">
              {ex.sets && `${ex.sets}×`}{displayReps}
            </span>
          </div>
        );
      })}

      {!logging ? (
        <button className="btn btn-primary" style={{marginTop:16}} onClick={() => setLogging(true)}>
          Log This Session
        </button>
      ) : (
        <div style={{marginTop:16}}>
          <div className="card-header">Log Results</div>
          {sessionExercises.map((ex, i) => {
            const isAmapMinusTwo = ex.reps === 'AMAP-2';
            return (
              <div key={i} className="card" style={{padding:12}}>
                <div style={{fontSize:13, fontWeight:600, marginBottom:4}}>
                  {ex.name} {ex.isAMAP && <span className="amap-badge">AMAP</span>}
                  {isAmapMinusTwo && amapMinusTwoTarget && (
                    <span style={{fontSize:12, color:'var(--success)', marginLeft:8}}>Target: {amapMinusTwoTarget}</span>
                  )}
                </div>
                {ex.lastRef && ex.lastRef.weight > 0 && (
                  <div style={{fontSize:11, color:'var(--text-muted)', marginBottom:6}}>
                    Last time: {formatWeight(ex.lastRef.weight)}kg × {ex.lastRef.reps}
                    {ex.weightHint?.type === 'heavier' && (
                      <span style={{color:'var(--amap-border)'}}> — aim heavier</span>
                    )}
                  </div>
                )}
                <div className="set-log-row">
                  <span className="set-label">Weight</span>
                  <input type="number" value={logData[i]?.weight ?? ''}
                    placeholder={ex.calculatedWeight || ex.rmSuggestion || ''}
                    onChange={e => handleLogChange(i, 'weight', e.target.value)}
                    onFocus={e => e.target.select()} />
                  <span style={{color:'var(--text-dim)', fontSize:13}}>kg</span>
                  <span className="set-label" style={{marginLeft:12}}>Reps</span>
                  <input type="number" value={logData[i]?.reps ?? ''}
                    placeholder={isAmapMinusTwo && amapMinusTwoTarget ? String(amapMinusTwoTarget) : ex.reps}
                    onChange={e => handleLogChange(i, 'reps', e.target.value)}
                    onFocus={e => e.target.select()} />
                </div>
                {(ex.isRPE) && (
                  <div style={{marginTop:8}}>
                    <span className="set-label">RPE {ex.rpeTarget && `(target: ${ex.rpeTarget})`}</span>
                    <div className="rpe-selector">
                      {[6,6.5,7,7.5,8,8.5,9,9.5,10].map(v => (
                        <button key={v} className={`rpe-btn ${logData[i]?.rpe === v ? 'selected' : ''}`}
                          onClick={() => handleLogChange(i, 'rpe', v)}>{v}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div className="card" style={{padding:12}}>
            <label>Session Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Fatigue, niggles, PRs, anything else..." />
          </div>

          <button className="btn btn-primary" onClick={handleSaveSession} disabled={saving}>
            {saving ? 'Saving...' : 'Save & Advance'}
          </button>
          <button className="btn btn-secondary" style={{marginTop:8}} onClick={() => setLogging(false)}>Cancel</button>
        </div>
      )}

      {/* Per-lift notes */}
      {(() => {
        const liftNote = (settings.liftNotes || {})[activeLift] || '';
        return (
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '10px 14px', marginTop: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: liftNote && !editingLiftNote ? 6 : 0 }}>
              <span className="card-header" style={{ marginBottom: 0 }}>Notes</span>
              {!editingLiftNote && (
                <button style={{
                  background: 'none', border: 'none', color: 'var(--accent)',
                  fontSize: 12, cursor: 'pointer', fontFamily: 'Barlow Condensed, sans-serif',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                }} onClick={() => { setEditingLiftNote(true); setLiftNoteDraft(liftNote); }}>
                  {liftNote ? 'Edit' : 'Add'}
                </button>
              )}
            </div>
            {editingLiftNote ? (
              <div>
                <textarea value={liftNoteDraft} onChange={e => setLiftNoteDraft(e.target.value)}
                  placeholder="e.g. Pinch shoulder blades, squeeze the barbell"
                  style={{ marginBottom: 8 }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary btn-small" style={{ flex: 1 }} onClick={() => {
                    const updated = { ...(settings.liftNotes || {}), [activeLift]: liftNoteDraft };
                    saveSettings({ liftNotes: updated });
                    setEditingLiftNote(false);
                    refresh();
                  }}>Save</button>
                  <button className="btn btn-secondary btn-small" style={{ flex: 1 }}
                    onClick={() => setEditingLiftNote(false)}>Cancel</button>
                </div>
              </div>
            ) : liftNote ? (
              <div style={{ fontSize: 13, color: 'var(--text-dim)', fontStyle: 'italic', lineHeight: 1.5 }}>
                {liftNote}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Tap "Add" to write a reminder for this lift</div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

function LiftSelector({ lifts, active, onSelect, settings }) {
  return (
    <div style={{display:'flex', gap:6, marginBottom:16}}>
      {lifts.map(lift => {
        const colors = { bench: 'var(--bench-color)', squat: 'var(--squat-color)', deadlift: 'var(--deadlift-color)' };
        const isActive = lift === active;
        return (
          <button key={lift} onClick={() => onSelect(lift)} style={{
            flex: 1, padding: '10px 0', borderRadius: 8,
            border: `1px solid ${isActive ? colors[lift] : 'var(--border)'}`,
            background: isActive ? `${colors[lift]}15` : 'none',
            color: isActive ? colors[lift] : 'var(--text-dim)',
            fontFamily: 'Barlow Condensed, sans-serif', fontSize: 14,
            fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
            cursor: 'pointer',
          }}>
            {lift.charAt(0).toUpperCase() + lift.slice(1)}
          </button>
        );
      })}
    </div>
  );
}
